import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { TigerwebBoundaryLevel, TigerwebSearchHit } from '../../../services/tigerwebBoundaryService'
import type {
  ControlCenterAssetKind,
  ControlCenterLicenseType,
  ControlCenterMunicipalityLevel,
  ControlCenterPersonKind,
} from '../../../types/controlCenter'

export type MapStyleId = 'streets' | 'terrain' | 'dark' | 'satellite'

export type EntityCategory = 'job' | 'person' | 'asset' | 'municipality'

/** Layers that can plot markers / map geometry. */
export type MapMarkerLayerId = 'jobs' | 'people' | 'assets' | 'municipalities'

export type MapMarkerVisibility = Record<MapMarkerLayerId, boolean>

/** Omni-driven map: selections plot themselves, so bulk layers start hidden. */
export const DEFAULT_MAP_MARKER_VISIBILITY: MapMarkerVisibility = {
  jobs: false,
  people: false,
  assets: false,
  municipalities: false,
}

export type MunicipalityLicenseStatusFilter = 'current' | 'expired' | 'expiring'

export type MappableEntity = {
  id: string
  category: EntityCategory
  lat: number
  lng: number
  color: string
  icon: IconDefinition
  title: string
  subtitle: string
  detail?: string
}

export type OmniCategory = 'job' | 'person' | 'asset' | 'municipality'

export type OmniSelection = {
  id: string
  category: OmniCategory
}

export type OmniSearchHit = {
  id: string
  category: OmniCategory
  name: string
  /** Secondary informative field shown under/beside the name in results. */
  detail: string
  color: string
  group: string
  /** Census place not present in the license database. */
  isExternal?: boolean
  censusHit?: TigerwebSearchHit
}

export type OmniJobFilters = {
  states: string[]
  projectTypes: string[]
  statuses: string[]
  activity: Array<'active' | 'inactive'>
}

export type OmniPersonFilters = {
  kinds: ControlCenterPersonKind[]
  clockedIn: Array<'yes' | 'no'>
}

export type OmniAssetFilters = {
  kinds: ControlCenterAssetKind[]
  statuses: string[]
}

export type OmniMunicipalityFilters = {
  states: string[]
  levels: ControlCenterMunicipalityLevel[]
  licenseTypes: ControlCenterLicenseType[]
  statuses: MunicipalityLicenseStatusFilter[]
}

export type BoundaryOverlayToggles = Record<TigerwebBoundaryLevel, boolean>

export type BoundaryShowAllLevel = 'none' | 'state' | 'county' | 'city'

